import React, { forwardRef } from 'react';
import { Button } from '@warp-ds/react';
import { variant } from './types';
import { IconFavorite16 } from './Icons';
import { i18n } from '@lingui/core';

type HeartButtonProps = {
    className?: string;
    variant: variant;
    onClick: () => void;
    toggled: boolean;
    numFavs: number;
};

const HeartButton = forwardRef(
    (props: HeartButtonProps, forwardRef: React.Ref<HTMLButtonElement>) => {
        const {
            className: externalClassname,
            onClick,
            toggled,
            variant,
            numFavs,
        } = props;

        let buttonProps = {};
        let iconProps: Record<string, string> = {};
        const buttonCommonStyling = `inline-flex items-center justify-center flex-row-reverse ${
            toggled ? 's-text-link' : ''
        } ${externalClassname ?? ''}`;

        switch (variant) {
            case 'heart':
                buttonProps = { pill: true, className: buttonCommonStyling };
                break;
            case 'heart-blue':
                buttonProps = { pill: true, className: buttonCommonStyling };
                iconProps = { stroke: 'var(--w-s-color-icon-primary)' };
                break;
            case 'heart-on-image':
                buttonProps = { pill: true, className: buttonCommonStyling };
                iconProps = {
                    stroke: 'var(--w-s-color-icon-inverted)',
                    fill: 'var(--w-s-color-icon-subtle)',
                };
                break;
            case 'button':
                buttonProps = {
                    utility: true,
                    quiet: true,
                    className: buttonCommonStyling,
                };
                break;
            case 'button-with-text':
                buttonProps = {
                    utility: true,
                    quiet: true,
                    className: `${buttonCommonStyling} whitespace-nowrap`,
                };
                break;
            case 'button-on-image':
                buttonProps = {
                    utility: true,
                    quiet: true,
                    className: `${buttonCommonStyling} whitespace-nowrap shadow-s`,
                };
                break;
        }

        iconProps = {
            ...iconProps,
            ariaLabel: toggled
                ? i18n.t({
                      id: 'heartIconAriaLabelToggled',
                      message: 'Icon of a filled heart',
                  })
                : i18n.t({
                      id: 'heartIconAriaLabel',
                      message: 'Icon of the outline of a heart',
                  }),
        };

        if (toggled) {
            iconProps.fill = 'var(--w-s-color-icon-primary)';
            iconProps.stroke = 'var(--w-s-color-icon-primary)';
        }

        const createButtonText = () =>
            toggled
                ? i18n.t({ id: 'heartButtonTextToggled' })
                : i18n.t({ id: 'heartButtonText' });

        return (
            <Button
                aria-haspopup="dialog"
                aria-pressed={toggled}
                onClick={onClick}
                ref={forwardRef}
                {...buttonProps}
            >
                {numFavs > 0 ? <span className="pl-4">{numFavs}</span> : null}
                <span
                    className={`ml-14 ${variant !== 'button-with-text' && 'sr-only'}`}
                >
                    {createButtonText()}
                </span>

                <IconFavorite16 {...iconProps} />
            </Button>
        );
    },
);

export default HeartButton;
